/**
 * Stacked progress bar for the GO Lab Datasets surfaces.
 *
 * Three segments — golden / in-progress / awaiting — sized proportionally
 * to the supplied counts. Zero-percentage segments are not rendered. Used
 * for both the collection-wide summary strip and per-channel cards;
 * default 6px height per `feature-spec.md` §2.3.
 */

import React from 'react';

import { cn } from '../../../../../../lib/utils';

interface Props {
  golden: number;
  inProgress: number;
  awaiting: number;
  height?: number;
  className?: string;
  'data-testid'?: string;
}

const StackedSegmentBar: React.FC<Props> = ({
  golden,
  inProgress,
  awaiting,
  height = 6,
  className,
  ...rest
}) => {
  const total = golden + inProgress + awaiting;
  if (total <= 0) {
    return (
      <div
        className={cn('w-full rounded bg-slate-200', className)}
        style={{ height }}
        {...rest}
      />
    );
  }
  const pct = (n: number) => (n / total) * 100;
  return (
    <div
      className={cn('flex w-full overflow-hidden rounded bg-slate-200', className)}
      style={{ height }}
      {...rest}
    >
      {golden > 0 && (
        <div
          className="bg-emerald-600 transition-all"
          style={{ width: `${pct(golden)}%` }}
          data-testid="segment-golden"
        />
      )}
      {inProgress > 0 && (
        <div
          className="bg-amber-600 transition-all"
          style={{ width: `${pct(inProgress)}%` }}
          data-testid="segment-in-progress"
        />
      )}
      {awaiting > 0 && (
        <div
          className="bg-slate-200 transition-all"
          style={{ width: `${pct(awaiting)}%` }}
          data-testid="segment-awaiting"
        />
      )}
    </div>
  );
};

export default StackedSegmentBar;
